/** Modale de changement de mot de passe admin. */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { authApi } from '../../services/api';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  /** Si true, l'utilisateur doit changer son mot de passe avant de continuer */
  forced?: boolean;
  onSuccess?: () => void;
}

export function ChangePasswordModal({
  open,
  onClose,
  forced,
  onSuccess,
}: ChangePasswordModalProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (vars: { current: string; next: string }) =>
      authApi.changePassword(vars.current, vars.next),
  });

  const handleSubmit = async () => {
    setError('');
    if (next.length < 8) {
      setError('Le nouveau mot de passe doit faire au moins 8 caractères');
      return;
    }
    if (next !== confirm) {
      setError('Les deux mots de passe ne correspondent pas');
      return;
    }
    try {
      await mutation.mutateAsync({ current, next });
      setCurrent('');
      setNext('');
      setConfirm('');
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  };

  return (
    <Modal
      open={open}
      onClose={forced ? () => {} : onClose}
      title={forced ? 'Changement de mot de passe requis' : 'Changer mon mot de passe'}
      footer={
        <>
          {!forced && (
            <Button variant="secondary" onClick={onClose}>
              Annuler
            </Button>
          )}
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Valider
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {forced && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2">
            Tu utilises encore le mot de passe par défaut (<code>changeme</code>). Change-le
            avant de continuer.
          </div>
        )}
        <Input
          label="Mot de passe actuel"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
        />
        <Input
          label="Nouveau mot de passe (min. 8 caractères)"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
        />
        <Input
          label="Confirmer le nouveau mot de passe"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
        {error && (
          <div className="text-xs bg-red-50 border border-red-200 text-red-700 rounded-md p-2">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
